import {PayloadAction, createSlice} from '@reduxjs/toolkit'
import {
  PaymentCategoryState,
  FetchPaymentCategoriesResponse,
  FetchPaymentCategoryByIdResponse,
  FetchAllPaymentCategoriesResponse,
} from './types'
import {
  fetchPaymentCategoryById,
  fetchPaymentCategories,
  createPaymentCategory,
  updatePaymentCategory,
  deletePaymentCategory,
  suspendPaymentCategory,
  activatePaymentCategory,
  fetchAllPaymentCategories,
} from './service'
import {ResponseError} from '../commonTypes'
import type {PaymentCategory} from '../../../models'
import {resolveBillingScheduleId} from '../../../components/billing/paymentCategoryBillingSchedule'

const initialState: PaymentCategoryState = {
  paymentCategoryForEdit: null,
  paymentCategories: [],
  total: 0,
  totalPages: 0,
  listLoading: false,
  actionLoading: false,
  error: null,
}
const paymentCategorySlice = createSlice({
  name: 'paymentCategory',
  initialState,
  reducers: {
    resetPaymentCategories: (state: PaymentCategoryState) => {
      state.paymentCategories = []
      state.total = 0
      state.totalPages = 0
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchPaymentCategories.pending, (state: PaymentCategoryState) => {
      state.listLoading = true
      state.error = null
    })
    builder.addCase(
      fetchPaymentCategories.rejected,
      (state: PaymentCategoryState, action: PayloadAction<any>) => {
        const {error} = action.payload
        state.error = error
        state.listLoading = false
      }
    )
    builder.addCase(
      fetchPaymentCategories.fulfilled,
      (state: PaymentCategoryState, action: PayloadAction<FetchPaymentCategoriesResponse>) => {
        state.listLoading = false
        state.error = null
        const {paymentCategories, total, totalPages} = action.payload
        state.paymentCategories = paymentCategories
        state.total = total
        state.totalPages = totalPages
      }
    )

    builder.addCase(fetchAllPaymentCategories.pending, (state: PaymentCategoryState) => {
      state.listLoading = true
      state.error = null
    })
    builder.addCase(
      fetchAllPaymentCategories.rejected,
      (state: PaymentCategoryState, action: PayloadAction<any>) => {
        const {error} = action.payload

        state.error = error
        state.listLoading = false
      }
    )
    builder.addCase(
      fetchAllPaymentCategories.fulfilled,
      (state: PaymentCategoryState, action: PayloadAction<FetchAllPaymentCategoriesResponse>) => {
        state.listLoading = false
        state.error = null
        const {paymentCategories} = action.payload
        state.paymentCategories = paymentCategories.map((c) => {
          const scheduleId = resolveBillingScheduleId(c.billingScheduleId)
          return scheduleId ? {...c, billingScheduleId: scheduleId} : {...c, billingScheduleId: undefined}
        })
      }
    )

    builder.addCase(fetchPaymentCategoryById.pending, (state: PaymentCategoryState) => {
      state.listLoading = true
      state.error = null
    })
    builder.addCase(
      fetchPaymentCategoryById.rejected,
      (state: PaymentCategoryState, action: PayloadAction<any>) => {
        const {error} = action.payload
        state.error = error
        state.listLoading = false
      }
    )
    builder.addCase(
      fetchPaymentCategoryById.fulfilled,
      (state: PaymentCategoryState, action: PayloadAction<FetchPaymentCategoryByIdResponse>) => {
        state.paymentCategoryForEdit = action.payload.paymentCategory
        state.listLoading = false
        state.error = null
        state.paymentCategoryForEdit = action.payload.paymentCategory
      }
    )

    builder.addCase(createPaymentCategory.pending, (state: PaymentCategoryState) => {
      state.actionLoading = true
      state.error = null
    })
    builder.addCase(
      createPaymentCategory.rejected,
      (state: PaymentCategoryState, action: PayloadAction<any>) => {
        const {error} = action.payload
        state.error = error
        state.actionLoading = false
      }
    )
    builder.addCase(
      createPaymentCategory.fulfilled,
      (state: PaymentCategoryState, action: PayloadAction<FetchPaymentCategoryByIdResponse>) => {
        state.paymentCategoryForEdit = action.payload.paymentCategory
        state.actionLoading = false
        state.error = null
      }
    )

    builder.addCase(updatePaymentCategory.pending, (state: PaymentCategoryState) => {
      state.actionLoading = true
      state.error = null
    })
    builder.addCase(
      updatePaymentCategory.rejected,
      (state: PaymentCategoryState, action: PayloadAction<any>) => {
        const {error} = action.payload
        state.error = error
        state.actionLoading = false
      }
    )
    builder.addCase(
      updatePaymentCategory.fulfilled,
      (state: PaymentCategoryState, action: PayloadAction<FetchPaymentCategoryByIdResponse>) => {
        const updated = action.payload.paymentCategory
        const scheduleId = resolveBillingScheduleId(updated.billingScheduleId)
        const normalized: PaymentCategory = {
          ...updated,
          billingScheduleId: scheduleId || undefined,
        }
        state.paymentCategoryForEdit = normalized
        const idx = state.paymentCategories.findIndex((c) => c._id === updated._id)
        if (idx >= 0) {
          state.paymentCategories[idx] = normalized
        }
        state.actionLoading = false
        state.error = null
      }
    )

    builder.addCase(deletePaymentCategory.pending, (state: PaymentCategoryState) => {
      state.actionLoading = true
      state.error = null
    })
    builder.addCase(
      deletePaymentCategory.rejected,
      (state: PaymentCategoryState, action: PayloadAction<any>) => {
        const {error} = action.payload
        state.error = error
        state.actionLoading = false
      }
    )
    builder.addCase(
      deletePaymentCategory.fulfilled,
      (state: PaymentCategoryState, action: PayloadAction<FetchPaymentCategoryByIdResponse>) => {
        state.paymentCategoryForEdit = action.payload.paymentCategory
        state.actionLoading = false
        state.error = null
      }
    )

    builder.addCase(suspendPaymentCategory.pending, (state: PaymentCategoryState) => {
      state.actionLoading = true
      state.error = null
    })
    builder.addCase(
      suspendPaymentCategory.rejected,
      (state: PaymentCategoryState, action: PayloadAction<any>) => {
        const {error} = action.payload
        state.error = error
        state.actionLoading = false
      }
    )
    builder.addCase(
      suspendPaymentCategory.fulfilled,
      (state: PaymentCategoryState, action: PayloadAction<FetchPaymentCategoryByIdResponse>) => {
        state.paymentCategoryForEdit = action.payload.paymentCategory
        state.actionLoading = false
        state.error = null
      }
    )

    builder.addCase(activatePaymentCategory.pending, (state: PaymentCategoryState) => {
      state.actionLoading = true
      state.error = null
    })
    builder.addCase(
      activatePaymentCategory.rejected,
      (state: PaymentCategoryState, action: PayloadAction<any>) => {
        const {error} = action.payload
        state.error = error
        state.actionLoading = false
      }
    )
    builder.addCase(
      activatePaymentCategory.fulfilled,
      (state: PaymentCategoryState, action: PayloadAction<FetchPaymentCategoryByIdResponse>) => {
        state.paymentCategoryForEdit = action.payload.paymentCategory
        state.actionLoading = false
        state.error = null
      }
    )
  },
})

export default paymentCategorySlice.reducer
export const actions = paymentCategorySlice.actions

